'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Chewing Gum Have Plastic? 10 Brands Checked"
      author="MicroPlastics Research Desk"
      date="December 15, 2025"
      readTime="10 min read"
      heroImage="/blog/heroes/chewing-gum-brands-ranked-microplastics.webp"
      heroImageAlt="Pieces of pastel chewing gum and a blister pack on a clean pale surface"
    >
      <BlogLastUpdated iso="2026-09-14" formatted="September 14, 2026" />

      <BlogTLDR>
        Most mainstream chewing-gum brands—including <strong>Trident, Extra,
        Orbit, 5 Gum, Stride and Mentos</strong>—list only &ldquo;gum base.&rdquo;
        US rules allow that umbrella term to include synthetic polymers, but a
        label does not reveal which formula a brand uses. Glee Gum, Simply Gum,
        Chicza and True Gum disclose chicle or another plant-derived base.
        That is useful material transparency, not proof of zero particles: the
        2025 UCLA pilot study found both synthetic and natural gums released
        microplastics in broadly similar ranges. This comparison ranks label
        disclosure and base material; it is not a brand-by-brand laboratory
        particle ranking.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Most major brands disclose only &ldquo;gum base,&rdquo; not the exact elastomers or resins in the formula.</>,
          <>FDA rules permit dozens of gum-base ingredients, including polyethylene, polyvinyl acetate and synthetic rubbers.</>,
          <>Glee Gum, Simply Gum, Chicza and True Gum disclose chicle or other plant-derived bases; PUR varies by product and should be checked individually.</>,
          <>The 2025 UCLA pilot found roughly 100 microplastic particles per gram on average and more than 600 per gram for one sample.</>,
          <>Natural and synthetic gums both shed in the pilot, so &ldquo;plant-based&rdquo; cannot honestly be translated into &ldquo;microplastic-free.&rdquo;</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The gum base label problem
      </h2>
      <p>
        When you read the ingredients list of a chewing gum, you&apos;ll see
        <em> &ldquo;gum base&rdquo;</em> listed, but not what&apos;s in it. The
        FDA permits more than 45 different ingredients under the &ldquo;gum
        base&rdquo; label (21 CFR 172.615), including:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Polyethylene</strong> (same plastic as plastic bags)</li>
        <li><strong>Polyvinyl acetate</strong> (used in white glue)</li>
        <li><strong>Styrene-butadiene rubber</strong> (used in car tyres)</li>
        <li><strong>Polyisobutylene</strong> (synthetic rubber)</li>
        <li><strong>Butyl rubber</strong> (used in inner tubes)</li>
        <li><strong>Paraffin wax</strong> (petroleum-derived)</li>
      </ul>
      <p>
        Manufacturers don&apos;t have to disclose which combination they use,
        and most don&apos;t. The default assumption for any mainstream brand
        is that the gum base is synthetic.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 2025 UCLA pilot study refresher
      </h2>
      <p>
        Sanjay Mohanty and Lisa Lowe at UCLA presented pilot data in March
        2025 measuring microplastic release from 10 popular gum brands.
        Findings from the pilot included:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Average ~100 microplastic particles per gram of gum.</li>
        <li>One outlier brand released over 600 particles/gram.</li>
        <li>94% of release happened in the first 8 minutes of chewing.</li>
        <li>Both synthetic-labelled AND natural-labelled gums released particles in similar ranges, &ldquo;natural&rdquo; gum base claims often aren&apos;t verified.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Major brands ranked
      </h2>

      <BlogComparisonTable
        caption="Chewing gum brands ranked by gum-base material (verified labels 2025-2026)"
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'base', label: 'Gum base type' },
          { key: 'rec', label: 'Recommendation' },
        ]}
        rows={[
          { brand: 'Glee Gum', base: 'Discloses chicle (sapodilla tree sap)', rec: 'High material transparency; not proven particle-free' },
          { brand: 'Simply Gum', base: 'Discloses chicle + plant ingredients', rec: 'High material transparency; not proven particle-free' },
          { brand: 'Chicza Mayan Organic', base: 'Discloses chicle', rec: 'High material transparency; not proven particle-free' },
          { brand: 'PUR Gum', base: 'Formula can vary by product', rec: 'Check the exact ingredient statement and current manufacturer disclosure' },
          { brand: 'True Gum', base: 'Discloses plant-derived gum base', rec: 'Higher transparency; certification does not equal a zero-particle test' },
          { brand: 'Trident', base: 'Lists gum base; exact composition undisclosed', rec: 'Synthetic polymers are permitted, but the label does not identify them' },
          { brand: 'Extra (Wrigley)', base: 'Lists gum base; exact composition undisclosed', rec: 'Synthetic polymers are permitted, but the label does not identify them' },
          { brand: 'Orbit (Wrigley)', base: 'Lists gum base; exact composition undisclosed', rec: 'Synthetic polymers are permitted, but the label does not identify them' },
          { brand: '5 Gum (Wrigley)', base: 'Lists gum base; exact composition undisclosed', rec: 'Synthetic polymers are permitted, but the label does not identify them' },
          { brand: 'Stride, Mentos and other major brands', base: 'Typically list gum base without its formula', rec: 'Treat the material as undisclosed, not as a measured particle ranking' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Where to buy chicle gum
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Whole Foods Market</strong>, usually carries Glee Gum and Simply Gum.</li>
        <li><strong>Sprouts</strong>. Glee Gum and Pur.</li>
        <li><strong>Amazon</strong>, all the recommended chicle brands.</li>
        <li><strong>Thrive Market</strong>. Simply Gum, Glee Gum.</li>
        <li><strong>Direct from brand websites</strong>. Chicza, Simply Gum, Glee Gum all sell direct, often with subscription savings.</li>
        <li><strong>European pharmacies</strong>. Chicza and True Gum widely available.</li>
        <li><strong>Most major US grocers</strong>, increasingly carry at least Simply Gum in the natural-foods aisle.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to read a gum label
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Look for &ldquo;chicle&rdquo; or specific tree gum.</strong> If the ingredient list says chicle, sapodilla, jelutong, or another specific tree resin, it&apos;s plant-based.</li>
        <li><strong>If it just says &ldquo;gum base,&rdquo; treat the material as undisclosed.</strong> Synthetic polymers are legally permitted, but the two words alone do not prove the exact recipe.</li>
        <li><strong>Verify &ldquo;natural&rdquo; or &ldquo;plant-based&rdquo; against the ingredient list.</strong> Even a real chicle disclosure is not proof of zero microplastic release.</li>
        <li><strong>Use biodegradability certification only for that claim.</strong> It can support end-of-life information; it is not a laboratory particle test.</li>
        <li><strong>Skip gum balls and vending machine gum</strong>, almost universally synthetic with no certification.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Alternatives to gum entirely
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Xylitol mints</strong> (Spry, PUR mints, B-Fresh), dental-positive without gum base.</li>
        <li><strong>Sugar-free hard candies</strong> from natural-foods brands.</li>
        <li><strong>Plain water + breath strips</strong> (organic options exist).</li>
        <li><strong>Whole fennel seeds or cardamom pods</strong>, traditional breath fresheners in many cultures.</li>
      </ul>

      <p>
        See related:{' '}
        <Link href="/blog/do-plastic-wrapped-candies-contain-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          the 2026 test of plastic-wrapped candies
        </Link>,{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best polymer-free toothpaste
        </Link>, and{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in toothpaste
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Check whether your gum hides a synthetic base"
        sub="Scan the exact pack in your pocket. MicroPlastics checks its gum-base wording, packaging and plant-based alternatives in seconds."
        buttonLabel="Scan my gum"
        source="chewing_gum_inline_scan"
        postSlug="chewing-gum-brands-ranked-microplastics"
        queryFamily="gum_plastic_brands"
      />

      <BlogFAQ
        items={[
          {
            question: 'Which chewing gum brands have no microplastics?',
            answer:
              'No brand in the current evidence can be called proven microplastic-free. Glee Gum, Simply Gum, Chicza and True Gum disclose chicle or another plant-derived base, but the 2025 UCLA pilot found that both natural and synthetic gums released particles. Material disclosure and measured particle release are separate questions.',
          },
          {
            question: 'Does Trident have plastic in it?',
            answer:
              'Trident lists "gum base" without disclosing its complete formula. FDA rules allow that term to include polyethylene, polyvinyl acetate, synthetic rubbers and other ingredients, but the label alone does not prove which specific polymers Trident currently uses.',
          },
          {
            question: 'Is sugar-free gum lower in microplastics?',
            answer:
              'There is no evidence that a sugar-free label predicts lower particle release. Sugar-free describes the sweetener, not the gum base. Check the base disclosure separately, and remember that the UCLA pilot detected particles from both synthetic and natural gums.',
          },
          {
            question: 'Are biodegradable chewing gums available?',
            answer:
              'Yes. True Gum (Danish brand, TÜV OK Biodegradable certified), Chicza (Mexican Mayan organic chicle), Simply Gum, and Glee Gum all biodegrade naturally because they\'re made from chicle tree sap rather than synthetic polymers.',
          },
          {
            question: 'Where can I buy plant-based chewing gum?',
            answer:
              'Whole Foods, Sprouts, Amazon, Thrive Market, and most natural-food aisles in major grocers carry Glee Gum and Simply Gum. Chicza and True Gum are easier to find online or in Europe. Most brands offer subscription savings direct.',
          },
          {
            question: 'How much microplastic do I avoid by switching gum?',
            answer:
              'Current evidence cannot support a yearly avoided-particle number for switching brands. The UCLA pilot found roughly 100 particles per gram on average, more than 600 per gram in one sample, and similar ranges for its natural and synthetic groups. Reducing how often you chew is more evidence-aligned than claiming a specific brand eliminates exposure.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Lowe LE, Mohanty SK',
            year: 2025,
            title: 'Chewing gum as a potential source of microplastic exposure: pilot study',
            publisher: 'American Chemical Society Spring Meeting / UCLA',
            url: 'https://www.acs.org/pressroom/newsreleases/2025/march/chewing-gum-releases-microplastics-pilot-study.html',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2024,
            title: 'CFR Title 21 Section 172.615: Chewing gum base',
            publisher: 'US FDA',
            url: 'https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfcfr/CFRSearch.cfm?fr=172.615',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2023,
            title: 'Food contact materials guidance: polymers in chewing gum',
            publisher: 'EFSA',
            url: 'https://www.efsa.europa.eu/en/topics/topic/food-contact-materials',
          },
          {
            authors: 'Beat the Microbead / Plastic Soup Foundation',
            year: 2024,
            title: 'Synthetic polymers in everyday products: database',
            publisher: 'Plastic Soup Foundation',
            url: 'https://www.beatthemicrobead.org/',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
